import {
    put,
    select,
    takeEvery,
    takeLatest,
    throttle,
    call
} from 'redux-saga/effects';
import {
    createCapiSession,
    clearCapiSession,
    SESSION_CREATION_ERROR,
    SET_CAPI_SESSION
} from 'capi/redux';
import { getCapiSessionID } from 'capi/redux/immutable';
import { v4 as uuid } from 'uuid';
import { getIsNewHomePageEnabled } from '../app/bootstrap';

import {
    getBuyFlow,
    getHostname,
    getReferrer,
    getUrlParams, pagePath
} from './utils';
import {
    clearStorefrontSession,
    setStorefrontSession,
    GET_OR_CREATE_STOREFRONT_SESSION,
    capiSessionCallIndicator,
    homePageIndicator
} from './sessionModule';
import { LOCATION_CHANGE, setBuyFlow } from '../routing/routingModule';
import { getBuyFlowSelector } from '../routing/routingSelectors';
import { logError, reportPageLoadError } from '../utils/errorUtils';
import { getCapiSessionCallIndicator, getStorefrontSessionKey } from './sessionSelectors';

const getCreateCapiSessionAction = (sessionKey, pagePathName, newBuyFlow) => createCapiSession({
    sessionKey,
    host: getHostname(),
    refUrl: getReferrer(),
    buyFlow: newBuyFlow,
    pagePath: pagePathName,
    ...getUrlParams()
});


function* getOrCreateSession() {
    const callInProgress = yield select(getCapiSessionCallIndicator);
    if (!callInProgress) {
        const sessionId = yield select(getCapiSessionID);
        if (!sessionId) {
            yield call(getOrCreateSessionKey);
            const sessionKey = yield select(getStorefrontSessionKey);
            yield put(capiSessionCallIndicator());
            const pagePathName = pagePath();
            const existingBuyFlow = yield select(getBuyFlowSelector);
            let newBuyFlow;
            if (!existingBuyFlow) {
                newBuyFlow = getBuyFlow();
                yield put(setBuyFlow(newBuyFlow));
            } else {
                yield put(setBuyFlow(existingBuyFlow));
                newBuyFlow = existingBuyFlow;
            }
            yield put(getCreateCapiSessionAction(sessionKey, pagePathName, newBuyFlow));
        }
    }
}

function* getOrCreateSessionKey() {
    let sessionKey = yield select(getStorefrontSessionKey);
    if (!sessionKey) {
        sessionKey = yield uuid();
        yield put(setStorefrontSession(sessionKey));
        const newHomePageEnabled = getIsNewHomePageEnabled();
        yield put(homePageIndicator(newHomePageEnabled));
    }
}

function* innerClearSessionSaga() {
    yield put(clearStorefrontSession());
    yield put(clearCapiSession());
}


export const createClearSessionSaga = (action) => {
    function* clearSessionSaga() {
        yield takeEvery(action, innerClearSessionSaga);
    }
    return clearSessionSaga;
};

export function* getOrCreateSessionSaga() {
    yield takeLatest(GET_OR_CREATE_STOREFRONT_SESSION, getOrCreateSession);
}

export function* sessionApiFailureWatcherSaga() {
    yield takeEvery(SESSION_CREATION_ERROR, function* sessionApiFailureSaga(action) {
        const { error } = action;
        logError(`Session Api failed with: ${error}`);
        reportPageLoadError('Session Api failed', `Session Api failed with: ${error}`);
        yield put(capiSessionCallIndicator(false));
    });
}

export function* sessionApiSuccessWatcherSaga() {
    yield takeEvery(SET_CAPI_SESSION, function* sessionApiSuccessSaga() {
        yield put(capiSessionCallIndicator(false));
    });
}

export function* checkSessionSaga() {
    yield throttle(1000, LOCATION_CHANGE, getOrCreateSession);
}
